import { useState } from "react";
import { FiX } from "react-icons/fi";
import Button from "@/components/ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/shadcn/select";
import { resolveAiRenderPreset } from "@/components/Scene3DEditor/aiRender";
import { GEN_AI_MODELS } from "@kagami/shared/sceneRenderModels";

// Purely a settings picker now — the actual render/generate flow lives on
// the viewport overlay (see AiRenderOverlay + useAiRender in
// Scene3DEditor/aiRender.js), triggered either by this modal's "Render"
// button or the toolbar's "Generate" button.
//
// Model and resolution are both held as local state and only pushed up to
// sceneSettings via onApply/onRender — closing via the X discards them, same
// as the resolution-only version of this modal did before. The aspect isn't
// chosen here at all — it's whatever the currently piloted camera is
// configured to in the Properties panel, scoped to whichever model is
// selected below.
export default function SceneAIRenderSettingsModal({
  onClose,
  viewportRef,
  nodes,
  genAiModelId,
  aiResolutionKey,
  onApply,
  onRender,
}) {
  const activeCameraId = viewportRef.current?.getActiveCameraId();
  const activeCameraNode = nodes?.find((n) => n._id === activeCameraId);
  const cameraAspectRatio = activeCameraNode?.aspect;

  const [modelId, setModelId] = useState(genAiModelId);
  const [resolutionKey, setResolutionKey] = useState(aiResolutionKey);

  const {
    aspectKey,
    resolutionOptions,
    resolutionKey: effectiveResolutionKey,
  } = resolveAiRenderPreset({
    genAiModelId: modelId,
    aiResolutionKey: resolutionKey,
    cameraAspectRatio,
  });

  const commit = () => ({
    genAiModelId: modelId,
    aiResolutionKey: effectiveResolutionKey,
  });

  return (
    <div
      id="SceneAIRenderSettingsModal"
      className="w-full max-w-[420px] rounded-md flex flex-col bg-background border">
      <div className="flex justify-between items-center py-2 px-4 border-b">
        <span className="text-lg font-bold">AI Render Settings</span>
        <button
          id="SceneAIRenderSettingsModal__closeTrigger"
          onClick={() => onClose(null)}
          className="text-secondary-foreground hover:text-white cursor-pointer">
          <FiX className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 flex flex-col gap-3">
        <div>
          <label className="text-[10px] text-secondary-foreground/50">
            Model
          </label>
          <Select value={modelId} onValueChange={setModelId}>
            <SelectTrigger
              id="SceneAIRenderSettingsModal__modelTrigger"
              className="text-xs h-8 w-full"
              title="Gen-AI render model">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GEN_AI_MODELS.map(({ model_id, model_name }) => (
                <SelectItem
                  id={`SceneAIRenderSettingsModal__modelOption-${model_id}`}
                  key={model_id}
                  value={model_id}
                  className="text-xs">
                  {model_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] text-secondary-foreground/50">
              Aspect ratio
            </label>
            <div className="text-xs h-8 flex items-center">{aspectKey}</div>
          </div>
          <div>
            <label className="text-[10px] text-secondary-foreground/50">
              Resolution
            </label>
            <Select
              value={effectiveResolutionKey}
              onValueChange={setResolutionKey}>
              <SelectTrigger
                id="SceneAIRenderSettingsModal__resolutionTrigger"
                className="text-xs h-8 w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {resolutionOptions.map((key) => (
                  <SelectItem
                    id={`SceneAIRenderSettingsModal__resolutionOption-${key}`}
                    key={key}
                    value={key}
                    className="text-xs">
                    {key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 p-2 border-t">
        <Button
          id="SceneAIRenderSettingsModal__doneTrigger"
          type="secondary"
          onClick={() => {
            onApply(commit());
            onClose(null);
          }}>
          Done
        </Button>
        <Button
          id="SceneAIRenderSettingsModal__renderTrigger"
          type="primary"
          onClick={() => {
            onRender(commit());
            onClose(null);
          }}>
          Render
        </Button>
      </div>
    </div>
  );
}
